import { TwoStepSetup } from "./twostep";
import { useEffect, useRef, useState } from "react";
import { ago, api, Avatar, can, Drawer, Loading, Pill, setQuery, useLoad, useMe, useRoute } from "./lib";

const NOTIFY: [string, string][] = [
  ["approval_request", "Someone asks me to review content"],
  ["approval_decision", "My content is approved, rejected, or needs changes"],
  ["task_assigned", "A task is assigned to me"],
  ["comment", "Someone comments on my work"],
  ["release_change", "A release my work depends on changes date"],
  ["decision_assigned", "Someone hands me a decision"],
  ["agent_result", "The agent finishes a draft I asked for"],
];

export function Settings() {
  const { me } = useMe();
  const route = useRoute();
  const tab = route.q.get("tab") || "me";
  return (
    <div className="page">
      <div className="tabs">
        <button className={tab === "me" ? "on" : ""} onClick={() => setQuery({ tab: null })}>My account</button>
        {can(me, "users.manage") && <button className={tab === "people" ? "on" : ""} onClick={() => setQuery({ tab: "people" })}>People</button>}
        {can(me, "users.manage") && <button className={tab === "roles" ? "on" : ""} onClick={() => setQuery({ tab: "roles" })}>Roles and permissions</button>}
        {can(me, "activity.view_all") && <button className={tab === "activity" ? "on" : ""} onClick={() => setQuery({ tab: "activity" })}>Activity</button>}
      </div>
      {tab === "people" ? <People /> : tab === "roles" ? <Roles /> : tab === "activity" ? <Activity /> : <MyAccount />}
    </div>
  );
}

function MyAccount() {
  const { me, refreshMe, toast } = useMe();
  const [pw, setPw] = useState({ current: "", next: "" });
  const [phone, setPhone] = useState(me.phone ?? "");
  const fileRef = useRef<HTMLInputElement>(null);
  const savePrefs = async (patch: Record<string, boolean>) => {
    await api("/api/me", { method: "PATCH", body: { notify_prefs: { ...me.notify_prefs, ...patch } } });
    refreshMe();
  };
  return (
    <div className="grid">
      <section className="card stack">
        <div className="row" style={{ gap: 14 }}>
          <Avatar id={me.id} name={me.name} hasPhoto={me.has_photo} size={64} />
          <div className="stack" style={{ gap: 6 }}>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/heic" style={{ display: "none" }} onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              const fd = new FormData();
              fd.set("file", f);
              try {
                await api("/api/me/photo", { method: "POST", body: fd });
                toast("Photo updated");
                refreshMe();
              } catch (err: any) {
                toast(err.message, true);
              }
              e.target.value = "";
            }} />
            <div className="row">
              <button className="btn sm" onClick={() => fileRef.current?.click()}>{me.has_photo ? "Change photo" : "Add a photo"}</button>
              {me.has_photo && <button className="btn sm ghost" onClick={async () => { await api("/api/me/photo", { method: "DELETE" }); refreshMe(); }}>Remove</button>}
            </div>
            <span className="muted tiny">Shows next to your comments and decisions.</span>
          </div>
        </div>
        <h2 style={{ marginTop: 8 }}>{me.name}</h2>
        <p className="sub" style={{ margin: 0 }}>{me.email} · {me.role_name} · Times shown in Central (America/Chicago)</p>
        <strong className="small" style={{ marginTop: 8 }}>Notify me in the portal when</strong>
        {NOTIFY.map(([k, label]) => (
          <label key={k} className="check">
            <input
              type="checkbox"
              checked={me.notify_prefs[k] !== false}
              onChange={async (e) => {
                await api("/api/me", { method: "PATCH", body: { notify_prefs: { ...me.notify_prefs, [k]: e.target.checked } } });
                refreshMe();
              }}
            />
            {label}
          </label>
        ))}
        <p className="muted tiny" style={{ margin: 0 }}>Email and Slack delivery, deadline reminders and weekly summaries come later and will be set here.</p>
      </section>
      <section className="card stack">
        <h2>Text me</h2>
        {!me.texts_ready ? (
          <div className="banner info small">Texts switch on once the carriers approve Momeaze's sending number, usually within 1 to 3 weeks. Your choices here are saved until then. <a href="/sms" target="_blank" rel="noreferrer">Text terms</a></div>
        ) : me.phone && !me.phone_verified ? (
          <PhoneVerify />
        ) : me.phone_verified ? (
          <div className="row between small" style={{ gap: 8 }}><span><Pill tone="good">Number confirmed</Pill> <span className="muted">At most 6 texts a day, none 8 PM to 7 AM. <a href="/sms" target="_blank" rel="noreferrer">Text terms</a></span></span><button className="btn sm" onClick={async () => { try { await api("/api/me/test-text", { method: "POST" }); toast("Test text sent"); } catch (e: any) { toast(e.message, true); } }}>Send me a test text</button></div>
        ) : null}
        <label className="field">Mobile number
          <div className="row">
            <input className="input" style={{ flex: 1 }} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="512-555-0123" />
            <button className="btn" onClick={async () => { try { await api("/api/me", { method: "PATCH", body: { phone } }); toast("Number saved"); refreshMe(); } catch (err: any) { toast(err.message, true); } }}>Save</button>
          </div>
        </label>
        <label className="check"><input type="checkbox" checked={!!me.notify_prefs.text_needs_me} onChange={(e) => savePrefs({ text_needs_me: e.target.checked })} /> Text me when something new needs my decision</label>
        <label className="check" style={{ marginLeft: 22 }}><input type="checkbox" disabled={!me.notify_prefs.text_needs_me} checked={!!me.notify_prefs.text_urgent_only} onChange={(e) => savePrefs({ text_urgent_only: e.target.checked })} /> Only when it's urgent or high priority</label>
        <span className="muted tiny">By ticking "Text me", you agree to get alert texts from Momeaze LLC at this number: up to 6 a day, none 8 PM to 7 AM Central. Message and data rates may apply. Reply STOP to stop or HELP for help. <a href="/sms" target="_blank" rel="noreferrer">Text terms and privacy</a></span>
      </section>
      <CalendarCard />
      <WorkEmailCard />
      <TwoStepCard />
      <section className="card">
        <h2>Change password</h2>
        <form
          className="form"
          style={{ marginTop: 12 }}
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await api("/api/me/password", { method: "POST", body: pw });
              setPw({ current: "", next: "" });
              toast("Password changed");
            } catch (err: any) {
              toast(err.message, true);
            }
          }}
        >
          <label className="field">Current password<input className="input" type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} required /></label>
          <label className="field">New password (12+ characters)<input className="input" type="password" autoComplete="new-password" minLength={12} value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} required /></label>
          <div><button className="btn">Change password</button></div>
        </form>
      </section>
    </div>
  );
}

function People() {
  const { me, toast } = useMe();
  const [invite, setInvite] = useState<any>(null);
  const { data: users, reload } = useLoad<any[]>("/api/users");
  const { data: roles } = useLoad<any>("/api/roles");
  const [adding, setAdding] = useState(false);
  const [created, setCreated] = useState<{ email: string; temporary_password: string } | null>(null);
  const [editing, setEditing] = useState<any>(null);
  if (!users || !roles) return <Loading />;
  const save = async (id: string, body: any) => {
    try {
      await api(`/api/users/${id}`, { method: "PATCH", body });
      toast("Saved");
      reload();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  return (
    <>
      {invite && (
        <Drawer title={`Sign-in for ${invite.name}`} sub="Shown once. Send it to them directly (Slack DM or text), not in a shared channel." onClose={() => setInvite(null)}>
          <div className="stack">
            <textarea className="input" readOnly rows={9} value={`Hi ${invite.name.split(" ")[0]}, you're set up on the Momeaze Portal, where the team keeps tasks, content, partners and what needs each of us in one place.\n\n1. Go to https://portal.momeazeapp.com\n2. Email: ${invite.email}\n3. One-time password: ${invite.temporary_password}\n\nYou'll choose your own password right after. Today shows what needs you; Tasks and your team's page have the rest.`} />
            <div className="row" style={{ gap: 8 }}>
              <button className="btn primary" onClick={(e) => navigator.clipboard.writeText((e.currentTarget.closest(".stack")!.querySelector("textarea") as HTMLTextAreaElement).value).then(() => toast("Invite copied"), () => toast("Couldn't copy", true))}>Copy invite</button>
              <button className="btn" onClick={() => setInvite(null)}>Done</button>
            </div>
          </div>
        </Drawer>
      )}

      <div className="row between">
        <p className="muted small" style={{ margin: 0 }}>Roles are starting templates. Add extra permissions per person, such as approval authority.</p>
        <button className="btn primary" onClick={() => setAdding(true)}>Add a person</button>
      </div>
      {created && (
        <div className="banner good">
          <div>
            <strong>{created.email} can sign in now.</strong> One-time password, shown only once: <code style={{ userSelect: "all" }}>{created.temporary_password}</code>. Share it privately; they'll set their own when they sign in.
            <div><button className="btn sm" style={{ marginTop: 8 }} onClick={() => setCreated(null)}>Done</button></div>
          </div>
        </div>
      )}
      <section className="card" style={{ padding: 0 }}>
        <div className="scroll-x">
          <table className="t">
            <thead><tr><th>Name</th><th className="hide-sm">Email</th><th>Role</th><th className="hide-sm">Department</th><th>Extra</th><th /></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} style={{ opacity: u.active ? 1 : 0.5 }}>
                  <td>{u.name}<div className="muted tiny">{u.title}</div></td>
                  <td className="hide-sm">{u.email}</td>
                  <td>
                    <select className="input" style={{ width: "auto" }} value={u.role_id} onChange={(e) => save(u.id, { role_id: e.target.value })}>
                      {roles.roles.map((r: any) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </td>
                  <td className="hide-sm">{u.department}</td>
                  <td>{u.extra_perms.length ? <Pill tone="accent">{u.extra_perms.length}</Pill> : <span className="muted">—</span>}</td>
                  <td className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
                    {u.has_2fa && u.id !== me?.id && <button className="btn ghost sm" title="If they lost their phone and recovery codes" onClick={async () => { if (!confirm(`Reset two-step sign-in for ${u.name}? They'll set it up again next time they sign in.`)) return; await api(`/api/users/${u.id}/2fa/reset`, { method: "POST" }); toast("Reset. They'll set it up again at sign-in."); reload(); }}>Reset two-step</button>}
                    {u.id !== me?.id && u.active && <button className="btn ghost sm" title="A one-time password and an invite to send them" onClick={async () => { if (!confirm(`Make a new one-time password for ${u.name}? Any password they set before stops working.`)) return; try { setInvite(await api(`/api/users/${u.id}/sign-in`, { method: "POST" })); } catch (e: any) { toast(e.message, true); } }}>Send sign-in</button>}
                    <button className="btn sm" onClick={() => setEditing(u)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {adding && (
        <Drawer title="Add a person" onClose={() => setAdding(false)}>
          <form
            className="form"
            onSubmit={async (e) => {
              e.preventDefault();
              const fd = Object.fromEntries(new FormData(e.currentTarget));
              try {
                const r = await api("/api/users", { method: "POST", body: fd });
                setCreated({ email: String(fd.email), temporary_password: r.temporary_password });
                setAdding(false);
                reload();
              } catch (err: any) {
                toast(err.message, true);
              }
            }}
          >
            <label className="field">Name<input className="input" name="name" required /></label>
            <label className="field">Work email<input className="input" name="email" type="email" required /></label>
            <div className="form-2">
              <label className="field">Title<input className="input" name="title" /></label>
              <label className="field">Department<input className="input" name="department" placeholder="marketing" /></label>
            </div>
            <label className="field">Role
              <select className="input" name="role_id" required>
                {roles.roles.map((r: any) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </label>
            <div className="row"><button className="btn primary">Add</button></div>
          </form>
        </Drawer>
      )}
      {editing && (
        <Drawer title={editing.name} onClose={() => setEditing(null)}>
          <form
            className="form"
            onSubmit={async (e) => {
              e.preventDefault();
              const fd = new FormData(e.currentTarget);
              await save(editing.id, {
                email: fd.get("email"),
                title: fd.get("title"),
                department: fd.get("department") || null,
                job_description: fd.get("job_description"),
                active: fd.get("active") === "on",
                extra_perms: fd.getAll("perm"),
              });
              setEditing(null);
            }}
          >
            <label className="field">Work email (used to sign in)<input className="input" name="email" type="email" required defaultValue={editing.email} /></label>
            <div className="form-2">
              <label className="field">Title<input className="input" name="title" defaultValue={editing.title} /></label>
              <label className="field">Department<input className="input" name="department" defaultValue={editing.department || ""} /></label>
            </div>
            <label className="field">
              Job description
              <textarea className="input" name="job_description" rows={6} defaultValue={editing.job_description || ""} placeholder="What they own, what they do every week, and what good looks like. Claude uses this to suggest their tasks." />
            </label>
            <label className="check"><input type="checkbox" name="active" defaultChecked={!!editing.active} /> Active (unchecking signs them out everywhere)</label>
            <strong className="small">Extra permissions on top of {roles.roles.find((r: any) => r.id === editing.role_id)?.name}</strong>
            <div className="stack" style={{ gap: 6 }}>
              {Object.entries(roles.permissions as Record<string, string>).map(([k, label]) => {
                const fromRole = roles.roles.find((r: any) => r.id === editing.role_id)?.perms.some((p: string) => p === k || p === "*");
                return (
                  <label key={k} className="check" style={{ opacity: fromRole ? 0.55 : 1 }}>
                    <input type="checkbox" name="perm" value={k} defaultChecked={fromRole || editing.extra_perms.includes(k)} disabled={fromRole} />
                    {label}{fromRole ? " (from role)" : ""}
                  </label>
                );
              })}
            </div>
            <div className="row"><button className="btn primary">Save</button></div>
          </form>
        </Drawer>
      )}
    </>
  );
}

function Roles() {
  const { toast } = useMe();
  const { data, reload } = useLoad<any>("/api/roles");
  const [editing, setEditing] = useState<any>(null);
  if (!data) return <Loading />;
  return (
    <>
      <div className="row between">
        <p className="muted small" style={{ margin: 0 }}>Every permission is checked on the server, including files and exports.</p>
        <button className="btn primary" onClick={() => setEditing({ id: null, name: "", description: "", perms: [] })}>New role</button>
      </div>
      <div className="grid">
        {data.roles.map((r: any) => (
          <section key={r.id} className="card stack">
            <div className="row between"><h2>{r.name}</h2>{r.id !== "owner" && <button className="btn sm" onClick={() => setEditing(r)}>Edit</button>}</div>
            <p className="sub" style={{ margin: 0 }}>{r.description}</p>
            <div className="row" style={{ gap: 6 }}>
              {r.perms.includes("*") ? <Pill tone="accent">Everything</Pill> : r.perms.map((p: string) => <Pill key={p}>{p}</Pill>)}
            </div>
          </section>
        ))}
      </div>
      {editing && (
        <Drawer title={editing.id ? `Edit ${editing.name}` : "New role"} onClose={() => setEditing(null)}>
          <form
            className="form"
            onSubmit={async (e) => {
              e.preventDefault();
              const fd = new FormData(e.currentTarget);
              const body = { name: fd.get("name"), description: fd.get("description"), perms: fd.getAll("perm") };
              try {
                await api(editing.id ? `/api/roles/${editing.id}` : "/api/roles", { method: editing.id ? "PATCH" : "POST", body });
                toast("Role saved");
                setEditing(null);
                reload();
              } catch (err: any) {
                toast(err.message, true);
              }
            }}
          >
            <label className="field">Name<input className="input" name="name" required defaultValue={editing.name} /></label>
            <label className="field">Description<input className="input" name="description" defaultValue={editing.description} /></label>
            <div className="stack" style={{ gap: 6 }}>
              {Object.entries(data.permissions as Record<string, string>).map(([k, label]) => (
                <label key={k} className="check"><input type="checkbox" name="perm" value={k} defaultChecked={editing.perms.includes(k)} />{label}</label>
              ))}
            </div>
            <div className="row"><button className="btn primary">Save role</button></div>
          </form>
        </Drawer>
      )}
    </>
  );
}

function Activity() {
  const { data } = useLoad<any[]>("/api/activity");
  if (!data) return <Loading />;
  return (
    <section className="card">
      <h2>Recent activity</h2>
      <p className="sub">Status changes, approvals, permission changes, uploads and exports.</p>
      <div className="timeline">
        {data.map((a) => (
          <div key={a.id}>
            <time>{new Date(a.created_at).toLocaleString("en-US", { timeZone: "America/Chicago", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</time>
            <span>{a.actor_name || "System"}: {a.action.replace(/\./g, " ").replace(/_/g, " ")}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function TwoStepCard() {
  const { me, refreshMe, toast } = useMe();
  const [setting, setSetting] = useState(false);
  const [code, setCode] = useState("");
  if (!me) return null;
  return (
    <section className="card stack">
      <div className="row between"><h2>Two-step sign-in</h2>{me.has_2fa ? <Pill tone="good">On</Pill> : <Pill>Off</Pill>}</div>
      {me.has_2fa ? (
        <form className="row" style={{ gap: 8, flexWrap: "wrap" }} onSubmit={async (e) => { e.preventDefault(); try { await api("/api/me/2fa", { method: "DELETE", body: { code } }); setCode(""); toast("Two-step sign-in is off"); refreshMe(); } catch (err: any) { toast(err.message, true); } }}>
          <span className="muted small" style={{ flexBasis: "100%" }}>After your password, the portal asks for the code in your authenticator app. To turn it off, enter a current code.</span>
          <input className="input" inputMode="numeric" placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value)} style={{ width: 160 }} />
          <button className="btn">Turn off</button>
        </form>
      ) : setting ? (
        <TwoStepSetup onDone={() => (setSetting(false), refreshMe(), toast("Two-step sign-in is on"))} onCancel={() => setSetting(false)} />
      ) : (
        <div className="stack">
          <span className="muted small">Adds a 6-digit code from your phone after your password, so a stolen password alone can't get in.</span>
          <div><button className="btn primary" onClick={() => setSetting(true)}>Turn on</button></div>
        </div>
      )}
    </section>
  );
}

// Opt-in: Claude reads the last two days of your work inbox each morning to suggest tasks. Only you see what comes from it.
function WorkEmailCard() {
  const { toast } = useMe();
  const { data, reload } = useLoad<any>("/api/suggestions");
  if (!data?.enabled) return null;
  const e = data.email;
  return (
    <section className="card stack">
      <div className="row between"><h2>Work email for suggestions</h2>{e.reading ? <Pill tone="good">On</Pill> : <Pill>Off</Pill>}</div>
      <span className="muted small">
        Each weekday morning Claude reads your team's plans and your job description and suggests a few tasks on Today. You decide: nothing reaches your list until you press Add.
        {e.reading
          ? " It also reads the last two days of your work inbox (sender, subject and Google's short preview), skipping promotions and newsletters. Only you see suggestions from your email. The portal doesn't keep your email, only the suggestion and a link back to it."
          : " Turn on email and it will also catch requests waiting in your work inbox. Only you would see those suggestions, and the portal never keeps your email."}
      </span>
      {!e.google_set_up ? (
        <span className="muted small">An admin needs to finish the Google setup for the portal first.</span>
      ) : e.reading ? (
        <div><button className="btn sm" onClick={async () => { await api("/api/me/email-reading", { method: "POST", body: { on: false } }); toast("Claude stopped reading your email"); reload(); }}>Stop reading my email</button></div>
      ) : (
        <div><a className="btn primary sm" href="/api/calendar/google/connect?gmail=1">Connect work email</a></div>
      )}
    </section>
  );
}

function CalendarCard() {
  const { toast } = useMe();
  const { data, reload } = useLoad<any>("/api/calendar");
  const route = useRoute();
  const msg = route.q.get("calendar");
  useEffect(() => {
    if (!msg) return;
    toast(msg === "connected" ? "Google Calendar connected" : msg, msg !== "connected");
    setQuery({ calendar: null });
    reload();
  }, [msg]);
  const c = data?.calendars;
  if (!c) return null;
  return (
    <section className="card stack">
      <div className="row between"><h2>Google Calendar and Chat</h2>{c.google === "connected" ? <Pill tone="good">Connected</Pill> : c.google === "error" ? <Pill tone="warn">Needs attention</Pill> : <Pill>Not connected</Pill>}</div>
      {c.google === "not_set_up" ? (
        <span className="muted small">An admin needs to finish the Google setup for the portal first.</span>
      ) : c.google === "connected" || c.google === "error" ? (
        <div className="stack">
          <span className="muted small">{c.email ? `${c.email}. ` : ""}Read-only. Your meetings show on your Calendar and Today with their titles; teammates see only that you're busy. {c.chat ? "Google Chat DMs and @mentions waiting on you reach Today; they're kept 3 days." : ""} {c.last_sync_at ? `Last checked ${ago(c.last_sync_at)}.` : ""}</span>
          {!c.chat && <div className="banner info small row between"><span>Add Google Chat, so DMs and @mentions waiting on you reach Today.</span><a className="btn sm" href="/api/calendar/google/connect">Add Google Chat</a></div>}
          {c.error && <div className="banner warn small">{c.error}</div>}
          <div className="row" style={{ gap: 8 }}>
            {c.error && <a className="btn primary sm" href="/api/calendar/google/connect">Connect again</a>}
            <button className="btn sm" onClick={async () => { if (!confirm("Disconnect Google Calendar? Your meetings leave the portal right away.")) return; await api("/api/calendar/google", { method: "DELETE" }); toast("Disconnected"); reload(); }}>Disconnect</button>
          </div>
        </div>
      ) : (
        <div className="stack">
          <span className="muted small">See your meetings next to deadlines and content, and get Google Chat DMs and @mentions waiting on you in Today. The portal only reads; it never changes, accepts or sends anything. Teammates see only that you're busy, never what. Chat messages are kept 3 days.</span>
          <div><a className="btn primary" href="/api/calendar/google/connect">Connect Google</a></div>
        </div>
      )}
    </section>
  );
}

function PhoneVerify() {
  const { refreshMe, toast } = useMe();
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (path: string, b?: object) => {
    setBusy(true);
    try {
      await api(path, { method: "POST", body: b });
      return true;
    } catch (e: any) {
      toast(e.message, true);
      return false;
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="banner info small stack" style={{ gap: 8 }}>
      <span>Confirm your number so texts can reach it.</span>
      {!sent ? (
        <div><button className="btn sm primary" disabled={busy} onClick={async () => (await go("/api/me/phone/verify-start")) && setSent(true)}>{busy ? "Sending…" : "Text me a code"}</button></div>
      ) : (
        <form className="row" style={{ gap: 6 }} onSubmit={async (e) => { e.preventDefault(); if (await go("/api/me/phone/verify", { code })) (toast("Number confirmed"), refreshMe()); }}>
          <input className="input" inputMode="numeric" autoComplete="one-time-code" placeholder="Code" value={code} onChange={(e) => setCode(e.target.value)} style={{ width: 120 }} autoFocus />
          <button className="btn sm primary" disabled={busy || !code.trim()}>Confirm</button>
          <button type="button" className="btn ghost sm" disabled={busy} onClick={() => go("/api/me/phone/verify-start")}>Send a new code</button>
        </form>
      )}
    </div>
  );
}
